import { ThemeMode, useProviderTheme } from './useProviderTheme';

type InputAppearance = 'filled-darker' | 'filled-lighter';

/**
 * Where the input is rendered, which decides how the filled appearance is picked
 * so the input contrasts with its surface:
 *
 * - `'page'` (default): the input sits on the normal page surface (the greyish
 *   `colorNeutralBackground2/3`). Light theme → `filled-lighter` (white) stands
 *   out on grey; dark theme → `filled-darker`.
 * - `'elevated'`: the input sits on an elevated surface (popover, dialog,
 *   flyout) which uses `colorNeutralBackground1`. In the light theme that is
 *   white, so the appearance flips to `filled-darker` to stay visible. In the
 *   dark theme it stays `filled-darker` as well, see below.
 */
export type InputSurface = 'page' | 'elevated';

/**
 * Returns the appropriate input appearance for the current theme and surface.
 *
 * The dark theme always uses `filled-darker`. Light and high-contrast use
 * `filled-lighter` on the page and `filled-darker` on an elevated surface (see
 * {@link InputSurface}).
 *
 * The theme is read from the FluentProvider context (body theme class), so this
 * works in both the components package and the app without a Redux dependency.
 */
export const useInputAppearance = (surface: InputSurface = 'page'): InputAppearance => {
  const { mode } = useProviderTheme();
  const dark = mode === ThemeMode.Dark;
  const elevated = surface === 'elevated';

  if (dark) {
    return 'filled-darker';
  }
  return elevated ? 'filled-darker' : 'filled-lighter';
};
